// Reports API layer.
//
// Every endpoint called here exists in services/api. Errors — a 404/501
// included — propagate to the page, which renders <ReportError />. They used
// to be coerced into a zeroed payload, which made a broken report
// indistinguishable from a quiet day (QA 8.1.4).

import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  AsyncReportEnvelope,
  CancellationTrendsData,
  CashierPerformanceData,
  CategorySalesData,
  DemandForecastData,
  DiscountAnalysisData,
  DiscountStaffRow,
  ExpenseAnalysisData,
  GstReportData,
  InputTaxData,
  Gstr1ReportData,
  KitchenPerformanceData,
  PayablesAgeingData,
  PaymentBreakdownData,
  PeakHoursData,
  PnlReportData,
  PurchaseRegisterData,
  RepeatVisitsData,
  ReportFilterValue,
  SalesReportData,
  StaffExpenseData,
  TableTurnoverData,
  TallyExportHistory,
  TallyExportRow,
  TopCustomersData,
  TopProductsData,
} from './types';
import { gstr1SectionSummary } from './gstr1';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `?${qs}`;
}

/**
 * Convert a GSTR1 period selector into the YYYY-MM-DD date range that the
 * backend `/reports/generate` endpoint expects.
 *   - monthly:   "2026-05"      → 2026-05-01 .. 2026-05-31
 *   - quarterly: "2026-Q1"      → 2026-01-01 .. 2026-03-31
 */
export function periodToDateRange(
  period: string,
  periodType: 'monthly' | 'quarterly',
): { start_date: string; end_date: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = (year: number, monthIdx: number) =>
    new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();

  if (periodType === 'monthly') {
    const [yearStr, monthStr] = period.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const start_date = `${yearStr}-${pad(month)}-01`;
    const end_date = `${yearStr}-${pad(month)}-${pad(lastDay(year, month - 1))}`;
    return { start_date, end_date };
  }

  // quarterly: accept either an explicit "YYYY-Q[1-4]" or a "YYYY-MM" month
  // (the month picker's format) and derive the quarter that contains it.
  let year: number;
  let quarter: number;
  if (period.includes('-Q')) {
    const [yearStr, qStr] = period.split('-Q');
    year = Number(yearStr);
    quarter = Number(qStr);
  } else {
    const [yearStr, monthStr] = period.split('-');
    year = Number(yearStr);
    quarter = Math.floor((Number(monthStr) - 1) / 3) + 1;
  }
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  const start_date = `${year}-${pad(startMonth)}-01`;
  const end_date = `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth - 1))}`;
  return { start_date, end_date };
}

function toRangeParams({ range, locationId }: ReportFilterValue): Record<string, string | undefined> {
  return {
    from: range.from,
    to: range.to,
    location_id: locationId,
  };
}

// ─── Empty fallbacks ─────────────────────────────────────────────────────────
// ─── API methods ─────────────────────────────────────────────────────────────

export const reportsApi = {
  // Sales — async (POST generate → poll GET :id) with a synchronous fallback to
  // the existing bills/reports/daily-summary endpoint for the table body.
  async getSalesDailySummary(filter: ReportFilterValue): Promise<SalesReportData> {
    return apiClient<SalesReportData>(
      `/bills/reports/daily-summary${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getGstSummary(filter: ReportFilterValue): Promise<GstReportData> {
    return apiClient<GstReportData>(`/bills/reports/gst-summary${buildQuery(toRangeParams(filter))}`);
  },

  async generateGstr1(period: string, periodType: 'monthly' | 'quarterly'): Promise<Gstr1ReportData> {
    const { start_date, end_date } = periodToDateRange(period, periodType);
    // Synchronous GSTR-1 — COMPLETED and RETURNED bills (credit notes net them
    // off). gst-parked 070 (D8): the API files them into B2B / B2CL / B2CS /
    // CDNR / CDNUR from the buyer's GSTIN; b2c_aggregate is no longer read here.
    const raw = await apiClient<
      Partial<Pick<Gstr1ReportData, 'b2b' | 'b2cl' | 'b2cs' | 'cdnr' | 'cdnur' | 'warnings'>> & {
        period: { start_date: string; end_date: string };
        total_invoices?: number;
      }
    >(`/bills/reports/gstr1${buildQuery({ start_date, end_date })}`);

    const data = {
      b2b: raw.b2b ?? [],
      b2cl: raw.b2cl ?? [],
      b2cs: raw.b2cs ?? [],
      cdnr: raw.cdnr ?? [],
      cdnur: raw.cdnur ?? [],
      total_invoices: Number(raw.total_invoices ?? 0),
    };
    const warnings = raw.warnings ?? [];
    return {
      period,
      period_type: periodType,
      sections: gstr1SectionSummary(data),
      incomplete_b2b_count: warnings.length,
      warnings,
      ...data,
    };
  },

  // Synchronous P&L (QA 8.4.1) — revenue less COGS, then APPROVED operating
  // expenses (QA 8.4.5) and stock written off as damage/wastage (QA 8.4.9)
  // to reach net profit.
  async getPnl(filter: ReportFilterValue): Promise<PnlReportData> {
    const raw = await apiClient<{
      total: {
        revenue: number;
        cogs: number;
        gross_profit: number;
        margin_pct: number;
        operating_expenses?: number;
        stock_write_offs?: number;
        exchange_gain_loss?: number;
        landed_cost_on_sold?: number;
        net_profit?: number;
        net_margin_pct?: number;
      };
    }>(
      `/bills/reports/pnl${buildQuery({
        start_date: filter.range.from,
        end_date: filter.range.to,
        location_id: filter.locationId,
      })}`,
    );
    const revenue = Number(raw.total.revenue);
    const cogs = Number(raw.total.cogs);
    const grossProfit = Number(raw.total.gross_profit);
    const operatingExpenses = Number(raw.total.operating_expenses ?? 0);
    const stockWriteOffs = Number(raw.total.stock_write_offs ?? 0);
    // Paying supplier-currency bills for more or less than their payable (multi-currency purchasing).
    const exchangeGainLoss = Number(raw.total.exchange_gain_loss ?? 0);
    // Freight added to stock cost that fell on units already sold (QA PU4-003).
    const landedCostOnSold = Number(raw.total.landed_cost_on_sold ?? 0);
    const netProfit = Number(
      raw.total.net_profit ?? grossProfit - operatingExpenses - stockWriteOffs + exchangeGainLoss - landedCostOnSold,
    );
    const hasData =
      revenue !== 0 || cogs !== 0 || operatingExpenses !== 0 || stockWriteOffs !== 0 || exchangeGainLoss !== 0 || landedCostOnSold !== 0;
    return {
      revenue,
      cogs,
      gross_profit: grossProfit,
      operating_expenses: operatingExpenses,
      stock_write_offs: stockWriteOffs,
      exchange_gain_loss: exchangeGainLoss,
      landed_cost_on_sold: landedCostOnSold,
      net_profit: netProfit,
      margin_pct: Number(raw.total.margin_pct),
      net_margin_pct: Number(
        raw.total.net_margin_pct ?? (revenue > 0 ? Math.round((netProfit / revenue) * 10000) / 100 : 0),
      ),
      lines: hasData
        ? [
            { category: 'Revenue', amount: revenue },
            { category: 'Cost of Goods Sold', amount: -cogs },
            { category: 'Gross Profit', amount: grossProfit },
            { category: 'Operating Expenses', amount: -operatingExpenses },
            { category: 'Stock Write-offs', amount: -stockWriteOffs },
            // Only a tenant that paid supplier-currency bills has this line.
            ...(exchangeGainLoss !== 0 ? [{ category: 'Exchange Gain/Loss', amount: exchangeGainLoss }] : []),
            ...(landedCostOnSold !== 0 ? [{ category: 'Landed cost on sold stock', amount: -landedCostOnSold }] : []),
            { category: 'Net Profit', amount: netProfit },
          ]
        : [],
    };
  },

  async getPaymentBreakdown(filter: ReportFilterValue): Promise<PaymentBreakdownData> {
    return apiClient<PaymentBreakdownData>(
      `/bills/reports/payment-breakdown${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getTopProducts(
    filter: ReportFilterValue,
    limit: number,
    sort: 'revenue' | 'quantity',
  ): Promise<TopProductsData> {
    return apiClient<TopProductsData>(
      `/bills/reports/top-products${buildQuery({ ...toRangeParams(filter), limit, sort })}`,
    );
  },

  async getCashierPerformance(filter: ReportFilterValue): Promise<CashierPerformanceData> {
    return apiClient<CashierPerformanceData>(
      `/bills/reports/cashier-performance${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getPeakHours(filter: ReportFilterValue): Promise<PeakHoursData> {
    return apiClient<PeakHoursData>(`/bills/reports/peak-hours${buildQuery(toRangeParams(filter))}`);
  },

  async getDiscountAnalysis(filter: ReportFilterValue): Promise<DiscountAnalysisData> {
    return apiClient<DiscountAnalysisData>(
      `/bills/reports/discount-analysis${buildQuery(toRangeParams(filter))}`,
    );
  },

  // Per-staff split of the same period's discounts.
  async getDiscountsByStaff(filter: ReportFilterValue): Promise<DiscountStaffRow[]> {
    const raw = await apiClient<{ per_cashier?: DiscountStaffRow[] }>(
      `/bills/reports/discounts${buildQuery({
        start_date: filter.range.from,
        end_date: filter.range.to,
        location_id: filter.locationId,
      })}`,
    );
    return raw.per_cashier ?? [];
  },

  // Purchase reports (QA PU4-010) — `purchase` plan feature.
  async getPurchaseRegister(filter: ReportFilterValue): Promise<PurchaseRegisterData> {
    return apiClient<PurchaseRegisterData>(`/purchase-reports/register${buildQuery(toRangeParams(filter))}`);
  },

  async getPayablesAgeing(locationId: string | undefined): Promise<PayablesAgeingData> {
    return apiClient<PayablesAgeingData>(`/purchase-reports/payables-ageing${buildQuery({ location_id: locationId })}`);
  },

  async getInputTax(filter: ReportFilterValue): Promise<InputTaxData> {
    return apiClient<InputTaxData>(`/purchase-reports/input-tax${buildQuery(toRangeParams(filter))}`);
  },

  async getCategorySales(filter: ReportFilterValue): Promise<CategorySalesData> {
    return apiClient<CategorySalesData>(
      `/bills/reports/category-sales${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getTopCustomers(filter: ReportFilterValue, limit: number): Promise<TopCustomersData> {
    return apiClient<TopCustomersData>(
      `/bills/reports/top-customers${buildQuery({ ...toRangeParams(filter), limit })}`,
    );
  },

  async getRepeatVisits(filter: ReportFilterValue): Promise<RepeatVisitsData> {
    return apiClient<RepeatVisitsData>(`/bills/reports/repeat-visits${buildQuery(toRangeParams(filter))}`);
  },

  async getTableTurnover(filter: ReportFilterValue): Promise<TableTurnoverData> {
    return apiClient<TableTurnoverData>(
      `/bills/reports/table-turnover${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getKitchenPerformance(filter: ReportFilterValue): Promise<KitchenPerformanceData> {
    return apiClient<KitchenPerformanceData>(
      `/bills/reports/kitchen-performance${buildQuery(toRangeParams(filter))}`,
    );
  },

  async getCancellationTrends(filter: ReportFilterValue): Promise<CancellationTrendsData> {
    const { range, locationId } = filter;
    const params: Record<string, string | undefined> = {
      start_date: range.from,
      end_date: range.to,
      location_id: locationId,
    };
    return apiClient<CancellationTrendsData>(`/reports/cancellation-trends${buildQuery(params)}`);
  },

  async getExpenseAnalysis(filter: ReportFilterValue): Promise<ExpenseAnalysisData> {
    const { range, locationId } = filter;
    const params: Record<string, string | undefined> = {
      start_date: range.from,
      end_date: range.to,
      location_id: locationId,
    };
    return apiClient<ExpenseAnalysisData>(`/expenses/reports/analysis${buildQuery(params)}`);
  },

  async getStaffExpense(filter: ReportFilterValue): Promise<StaffExpenseData> {
    return apiClient<StaffExpenseData>(
      `/expenses/reports/staff-tracking${buildQuery(toRangeParams(filter))}`,
    );
  },

  // The list is a paginated envelope ({ data, meta.total }) of API rows (`file_size`, lowercase
  // `format`). Reading it with `apiClient` handed the page a bare array as `{ rows, total }`, so
  // `rows` was undefined and the whole Tally page rendered "Unable to load report" (QA 8.5.8).
  async getTallyExports(page: number, limit = 20): Promise<TallyExportHistory> {
    const { data, meta } = await apiClientPaginated<
      Array<Omit<TallyExportRow, 'format' | 'size_bytes' | 'download_url'> & {
        format: string;
        file_size: number | null;
        download_url: string | null;
      }>
    >(`/reports/tally-export${buildQuery({ page, limit })}`);
    return {
      rows: data.map(({ file_size, download_url, ...r }) => ({
        ...r,
        format: r.format.toUpperCase() as TallyExportRow['format'],
        size_bytes: Number(file_size ?? 0),
        record_count: Number(r.record_count ?? 0),
        download_url: download_url ?? undefined,
      })),
      total: meta.total,
    };
  },

  async generateTallyExport(params: {
    from: string;
    to: string;
    format: 'XML' | 'CSV';
    voucher_types: string[];
    location_id?: string;
    /** Ledgers the cash-rounding journals post to; blank = the API defaults. */
    rounding_gain_ledger?: string;
    rounding_loss_ledger?: string;
  }): Promise<TallyExportRow> {
    const body = {
      start_date: params.from,
      end_date: params.to,
      format: params.format.toLowerCase(),
      // The DTO accepts only lowercase voucher types (QA 8.6.13).
      voucher_types: params.voucher_types.map((t) => t.toLowerCase()),
      ...(params.location_id ? { location_id: params.location_id } : {}),
      ...(params.rounding_gain_ledger?.trim() ? { rounding_gain_ledger: params.rounding_gain_ledger.trim() } : {}),
      ...(params.rounding_loss_ledger?.trim() ? { rounding_loss_ledger: params.rounding_loss_ledger.trim() } : {}),
    };
    return apiClient<TallyExportRow>(`/reports/tally-export`, { method: 'POST', body });
  },

  async pollAsyncReport<T>(id: string): Promise<AsyncReportEnvelope<T>> {
    return apiClient<AsyncReportEnvelope<T>>(`/reports/${id}`);
  },

  // The endpoint takes only location_id (forbidNonWhitelisted rejects anything
  // else) and answers with the PO it created (QA 8.6.13).
  async autoGeneratePO(params: {
    location_id?: string;
  }): Promise<{ id: string; po_number: string; status: string }> {
    return apiClient<{ id: string; po_number: string; status: string }>(
      `/purchase-orders/auto-generate`,
      { method: 'POST', body: { location_id: params.location_id } },
    );
  },

  async getDemandForecast(params: {
    festival_id?: string;
    date?: string;
    location_id?: string;
  }): Promise<DemandForecastData> {
    // Festival forecast has priority over daily production plan
    const path = params.festival_id
      ? `/festivals/${params.festival_id}/forecast${buildQuery({ location_id: params.location_id })}`
      : `/production-plan${buildQuery({ date: params.date, location_id: params.location_id })}`;
    return apiClient<DemandForecastData>(path);
  },
};
